import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Person } from '../../types';
import type {
  CandidateDetailsResponse,
  CandidateExperienceEntry,
  CandidateSkillEntry,
  CandidateDomainEntry,
  CandidateEducationEntry,
  CandidateCertificationEntry,
  CvTextLayer,
  ReviewStatus,
} from '../../types/candidateDetails';
import { profileUpdateApi } from '../../services/profileUpdateApi';
import { peopleApi, adminApi } from '../../services/api';
import {
  listPoolMembershipsForPerson,
  type PersonPoolMembership,
} from '../../services/candidatePoolsApi';
import { ProfileAdminTab } from './ProfileAdminTab';
import { useAuth } from '../../hooks/useAuth';
import { ProfileHeaderEmailField } from './ProfileHeaderEmailField';
import { ProfileUpdateLinkHelpText } from './ProfileUpdateLinkHelpText';
import { profileUpdateLinkExpiresInDays } from '../../utils/profileUpdateLink';
import { looksLikeEmail } from '../../utils/looksLikeEmail';
import { adminProfileApi, selfMagicLinkApi, selfSessionApi, type ProfileMutationsApi } from '../../services/profileMutationsApi';
import { CandidateOverviewEditDrawer } from './CandidateOverviewEditDrawer';
import { ExperienceEditorModal } from './ExperienceEditorModal';
import { SkillsEditorModal } from './SkillsEditorModal';
import { LanguagesEditorModal } from './LanguagesEditorModal';
import { EducationEditorModal } from './EducationEditorModal';
import { CertificationsEditorModal } from './CertificationsEditorModal';
import { useCandidateSourceLayers } from '../../hooks/useCandidateSourceLayers';
import { usePersonCvUpload, type UsePersonCvUploadResult } from '../../hooks/usePersonCvUpload';
import { ImportProgressPanel } from '../CandidatePools/ImportProgressPanel';
import {
  ProfileHeaderUpdateActions,
  ProfileOverviewUpdateActions,
} from './ProfileOverviewUpdateActions';
import { showProfileUpdateActions, profileTabsForViewer } from '../../utils/profileCvUpload';

/**
 * Candidate profile modal (Phase 3D/3C) — read-only view for external_candidate
 * profiles. Six tabs: Overview / Experience / Skills / Education / Pools &
 * Source / Admin. Edit flows land in Phase 3E.
 *
 * Wireframe: docs/designs/candidate-profile-modal.svg
 */

export type CandidateProfileTab =
  | 'overview'
  | 'experience'
  | 'skills'
  | 'education'
  | 'pools_source'
  | 'admin';

import type { ViewerContext } from '../../types/profileViewerContext';
export type { ViewerContext };

const TABS: Array<{ id: CandidateProfileTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'experience', label: 'Experience' },
  { id: 'skills', label: 'Skills' },
  { id: 'education', label: 'Education' },
  { id: 'pools_source', label: 'Pools & Source' },
  { id: 'admin', label: 'Admin' },
];

/**
 * Phase 3F — review-queue context. Present when the modal was opened from the
 * pool page's "Start queue" / per-row Review CTA. Parent owns the ids list;
 * the modal just renders navigation + the footer action bar.
 */
export interface CandidateReviewQueueContext {
  /** Ordered list of personIds in the active pool backlog. */
  ids: number[];
  /** 0-based index of the currently-displayed person in `ids`. */
  index: number;
  /** Called when the user clicks Prev in the header stepper. */
  onPrev: () => void;
  /**
   * Called when the user clicks Next in the header stepper or after a
   * successful "Mark reviewed" action on a non-last item.
   */
  onNext: () => void;
  /**
   * Optional — fired when the queue is exhausted (last item marked reviewed
   * or the user hits Next on the last item). Defaults to closing the modal
   * when omitted.
   */
  onComplete?: () => void;
}

interface CandidateProfileModalProps {
  person: Person;
  viewerContext?: ViewerContext;
  onClose: () => void;
  /** Optional: close the modal and route to a candidate pool detail page. */
  onNavigateToCandidatePool?: (poolId: number) => void;
  /** Test-only: force initial tab (keeps RTL deterministic). */
  initialTab?: CandidateProfileTab;
  /** Phase 3F — enables queue-mode (stepper + footer action bar). */
  reviewQueue?: CandidateReviewQueueContext;
  /**
   * Phase 3F — called after a successful review-status PATCH so the parent
   * can refresh its members list / needs_review counters.
   */
  onReviewStatusChanged?: (updated: Person) => void;
  /**
   * Phase 3E-1 — called after a successful overview PATCH so the parent can
   * refresh pool lists and the global people cache. When omitted, the modal
   * still updates its own local state.
   */
  onPersonUpdated?: (updated: Person) => void;
  /** Called after archive or remove-from-last-pool; parent should refresh lists and close. */
  onArchived?: () => void;
  /**
   * Phase 3E-1 — when true, the Edit overview drawer exposes admin-only
   * inputs (e.g. `availability_date`). Non-admins are still able to edit
   * the other overview fields; they just don't see the admin-gated ones.
   */
  isAdmin?: boolean;
  /**
   * `overlay` (default): full-screen modal with backdrop. `embedded`: inline
   * panel (profile-update page) — no backdrop, no dismiss X, footer stays in card.
   */
  presentation?: 'overlay' | 'embedded';
  /** Self-service magic link — called only from "Finalize and Close", not X. */
  onFinalize?: () => void;
  /** Shared CV upload (profile-update page — one progress UI for card + modal). */
  cvUpload?: UsePersonCvUploadResult;
}

export const CandidateProfileModal: React.FC<CandidateProfileModalProps> = ({
  person: personProp,
  viewerContext = { kind: 'admin' },
  onClose,
  onNavigateToCandidatePool,
  initialTab = 'overview',
  reviewQueue,
  onReviewStatusChanged,
  onPersonUpdated,
  onArchived,
  isAdmin = false,
  presentation = 'overlay',
  onFinalize,
  cvUpload: cvUploadProp,
}) => {
  const isEmbedded = presentation === 'embedded';
  const [enriched, setEnriched] = useState<Person>(() => {
    if (personProp.CandidateDetails) return personProp;
    return {
      ...personProp,
      CandidateDetails: {
        professionalSummary: null,
        seniorityLevel: null,
        educationLevel: null,
        yearsExperienceTotal: null,
        availability: { status: null, date: null, noticePeriodWeeks: null },
        rate: { country: null, expectationMin: null, expectationMax: null, currency: null },
        locationModelPreferences: [],
        employmentTypePreferences: [],
        workAuthorizationRegions: [],
        reviewStatus: 'unreviewed',
        reviewNotes: null,
        reviewedAt: null,
        reviewedBy: null,
        parserVersion: null,
        parseConfidence: null,
        experiences: [],
        education: [],
        certifications: [],
        skills: [],
        languages: [],
        targetRoles: [],
        domains: [],
        sourceMetadata: { poolId: null, originalFilename: null, parsedAt: null },
      },
    };
  });
  const [activeTab, setActiveTab] = useState<CandidateProfileTab>(initialTab);
  const overviewTopRef = useRef<HTMLDivElement | null>(null);
  const reviewActionBarRef = useRef<HTMLDivElement | null>(null);
  const markReviewedBtnRef = useRef<HTMLButtonElement | null>(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [experienceEditorOpen, setExperienceEditorOpen] = useState(false);
  const [skillsEditorOpen, setSkillsEditorOpen] = useState(false);
  const [educationEditorOpen, setEducationEditorOpen] = useState(false);
  const [certificationsEditorOpen, setCertificationsEditorOpen] = useState(false);
  const [languagesEditorOpen, setLanguagesEditorOpen] = useState(false);

  const visibleTabs = React.useMemo(
    () => profileTabsForViewer(viewerContext),
    [viewerContext]
  );

  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab('overview');
    }
  }, [activeTab, visibleTabs]);

  const apiClient: ProfileMutationsApi = React.useMemo(() => {
    switch (viewerContext?.kind) {
      case 'self_magic':
        return selfMagicLinkApi(viewerContext.token, viewerContext.jwt);
      case 'self_session':
        return selfSessionApi;
      case 'admin':
      case 'hr':
      default:
        return adminProfileApi;
    }
  }, [viewerContext]);

  useEffect(() => {
    setEnriched(personProp);
    setReviewError(null);
  }, [personProp]);

  useEffect(() => {
    if (personProp.id == null) return;
    let cancelled = false;
    peopleApi
      .getById(personProp.id)
      .then((full) => {
        if (!cancelled) setEnriched((prev) => ({ ...prev, ...full }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [personProp.id]);

  const details: CandidateDetailsResponse | undefined = enriched.CandidateDetails;
  const reviewStatus: ReviewStatus = details?.reviewStatus ?? 'unreviewed';

  const refreshPersonFromServer = React.useCallback(async () => {
    if (enriched.id == null) return;
    try {
      const full = await peopleApi.getById(enriched.id);
      setEnriched((prev) => ({ ...prev, ...full }));
      onPersonUpdated?.(full);
    } catch {
      /* keep current row */
    }
  }, [enriched.id, onPersonUpdated]);

  const cvUploadInternal = usePersonCvUpload({
    personId: enriched.id,
    viewerContext,
    onImported: refreshPersonFromServer,
    enabled: cvUploadProp == null,
  });
  const cvUpload = cvUploadProp ?? cvUploadInternal;

  const openOverviewEdit = React.useCallback(() => setEditDrawerOpen(true), []);

  const queueTotal = reviewQueue?.ids.length ?? 0;
  const isLastInQueue = reviewQueue ? reviewQueue.index >= queueTotal - 1 : false;

  const handleMarkReviewed = async () => {
    if (!enriched.id || reviewBusy) return;
    setReviewBusy(true);
    setReviewError(null);
    try {
      const updated = await peopleApi.patchReviewStatus(enriched.id, {
        review_status: 'reviewed',
      });
      setEnriched((prev) => ({ ...prev, ...updated }));
      onReviewStatusChanged?.(updated);
      if (reviewQueue) {
        if (isLastInQueue) {
          (reviewQueue.onComplete ?? onClose)();
        } else {
          reviewQueue.onNext();
        }
      }
    } catch (e: any) {
      setReviewError(e?.response?.data?.error || e?.message || 'Failed to mark reviewed');
    } finally {
      setReviewBusy(false);
    }
  };

  const handleNeedsMoreWork = () => {
    if (!reviewQueue || reviewBusy) return;
    // Skip without patching — row is already needs_review. Advance or close.
    if (isLastInQueue) {
      (reviewQueue.onComplete ?? onClose)();
    } else {
      reviewQueue.onNext();
    }
  };

  const panelClass = isEmbedded
    ? 'w-full overflow-y-auto'
    : 'bg-dark-card border border-dark-border rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto';

  const profilePanel = (
    <div
      className={panelClass}
      onClick={isEmbedded ? undefined : (e: React.MouseEvent) => e.stopPropagation()}
      role={isEmbedded ? 'region' : 'dialog'}
      aria-modal={isEmbedded ? undefined : true}
      aria-label={`Candidate profile — ${enriched.Name || 'Unknown'}`}
    >
        <CandidateModalHeader
          person={enriched}
          viewerContext={viewerContext}
          details={details}
          onClose={onClose}
          showCloseButton={!isEmbedded}
          reviewQueue={reviewQueue}
          cvUpload={cvUpload}
          onContactSaved={(updated) => {
            setEnriched((prev) => ({ ...prev, ...updated }));
            onPersonUpdated?.(updated);
          }}
        />

        {reviewStatus === 'needs_review' && (
          <ReviewBanner
            details={details!}
            onReviewNow={() => {
              // "Open review-decision mode" = scroll the footer action bar
              // into view and focus its primary button so the reviewer can
              // immediately confirm or flag. Previously this jumped to the
              // Overview tab, which did nothing useful because the decision
              // buttons live at the bottom of the modal.
              reviewActionBarRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
              // Focus after the scroll has started so screen readers and
              // keyboard users land on the actionable button, not mid-air.
              setTimeout(() => {
                markReviewedBtnRef.current?.focus();
              }, 200);
            }}
          />
        )}

        <TabBar activeTab={activeTab} onSelect={setActiveTab} visibleTabs={visibleTabs} />

        {cvUpload.importState.phase !== 'idle' && !isEmbedded && (
          <div className="px-6 pt-4 border-b border-dark-border bg-dark-card/80">
            <ImportProgressPanel
              state={cvUpload.importState}
              onCancel={cvUpload.cancelImport}
              onDismiss={cvUpload.resetImport}
              onRetry={cvUpload.retryUpload}
              context="profile"
            />
          </div>
        )}

        <div className="p-6" data-testid={`candidate-profile-tab-panel-${activeTab}`}>
          <div ref={overviewTopRef} />
          {activeTab === 'overview' && (
            <OverviewTab
              person={enriched}
              details={details}
              viewerContext={viewerContext}
              onEditKeyFacts={openOverviewEdit}
              cvUpload={cvUpload}
              onEditLanguages={() => setLanguagesEditorOpen(true)}
            />
          )}
          {activeTab === 'experience' && (
            <ExperienceTab
              person={enriched}
              details={details}
              viewerContext={viewerContext}
              onEdit={() => setExperienceEditorOpen(true)}
            />
          )}
          {activeTab === 'skills' && (
            <SkillsTab
              person={enriched}
              details={details}
              viewerContext={viewerContext}
              onEdit={() => setSkillsEditorOpen(true)}
            />
          )}
          {activeTab === 'education' && (
            <EducationTab
              person={enriched}
              details={details}
              viewerContext={viewerContext}
              onEditEducation={() => setEducationEditorOpen(true)}
              onEditCertifications={() => setCertificationsEditorOpen(true)}
            />
          )}
          {activeTab === 'pools_source' && (
            <PoolsSourceTab
              person={enriched}
              details={details}
              onNavigateToCandidatePool={onNavigateToCandidatePool}
            />
          )}
          {activeTab === 'admin' && (
            <ProfileAdminTab
              person={enriched}
              details={details}
              isAdmin={isAdmin}
              onPersonUpdated={onPersonUpdated}
              onArchived={onArchived ?? onClose}
              onNavigateToCandidatePool={onNavigateToCandidatePool}
            />
          )}
        </div>

        {/*
         * The review action bar is now shown whenever there is a reviewable
         * decision to make: either the modal is driven by a queue, or the
         * candidate currently has status=needs_review (independent of how
         * the modal was opened). This fixes the case where a reviewer opens
         * a needs_review candidate from the pool table or a direct link and
         * has no Mark-reviewed / Discard buttons.
         */}
        {(reviewQueue || reviewStatus === 'needs_review') && (
          <div ref={reviewActionBarRef}>
            <ReviewActionBar
              reviewStatus={reviewStatus}
              busy={reviewBusy}
              error={reviewError}
              isLast={isLastInQueue}
              inQueue={!!reviewQueue}
              onMarkReviewed={handleMarkReviewed}
              onNeedsMoreWork={handleNeedsMoreWork}
              markReviewedBtnRef={markReviewedBtnRef}
            />
          </div>
        )}


        {viewerContext?.kind === 'self_magic' && (
          <div className="p-6 border-t border-dark-border flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={async () => {
                if (
                  window.confirm(
                    'Are you sure you want to finalize your profile? You will not be able to edit it again using this link.'
                  )
                ) {
                  try {
                    await profileUpdateApi.submitProfile(
                      { token: viewerContext.token },
                      viewerContext.jwt
                    );
                    (onFinalize ?? onClose)();
                  } catch (e) {
                    console.error('Failed to finalize profile:', e);
                    alert('Failed to finalize profile. Please try again.');
                  }
                }
              }}
              className="w-full max-w-sm px-8 py-3 rounded-xl font-bold bg-[#00B074] hover:bg-[#009663] text-white shadow-lg transition-all"
              data-testid="candidate-profile-finalize"
            >
              Finalize and Close
            </button>
            <p className="text-xs text-dark-text-secondary text-center max-w-md">
              Save your changes and finish. Your profile-update link will stop working after you finalize.
            </p>
          </div>
        )}

        <ProvenanceFooter details={details} />
    </div>
  );


  const editorLayers = (
    <>
      {editDrawerOpen && (
        <CandidateOverviewEditDrawer
          person={enriched}
          apiClient={apiClient}
          isAdmin={isAdmin}
          allowEditContactEmail={
            viewerContext.kind !== 'self_magic' && viewerContext.kind !== 'self_session'
          }
          onClose={() => setEditDrawerOpen(false)}
          onSaved={(updated) => {
            setEnriched((prev) => {
              const next = { ...prev, ...updated };
              const summary = updated.CandidateDetails?.professionalSummary;
              if (summary !== undefined && summary !== null && prev.CandidateDetails) {
                next.CandidateDetails = {
                  ...prev.CandidateDetails,
                  ...updated.CandidateDetails,
                  professionalSummary: summary,
                };
              }
              return next;
            });
            onPersonUpdated?.(updated);
            setEditDrawerOpen(false);
          }}
        />
      )}

      {experienceEditorOpen && (
        <ExperienceEditorModal
          person={enriched}
          apiClient={apiClient}
          onClose={() => setExperienceEditorOpen(false)}
          onSaved={(updated) => {
            setEnriched((prev) => ({ ...prev, ...updated }));
            onPersonUpdated?.(updated);
            setExperienceEditorOpen(false);
          }}
        />
      )}

      {skillsEditorOpen && (
        <SkillsEditorModal
          person={enriched}
          apiClient={apiClient}
          isAdmin={isAdmin}
          onClose={() => setSkillsEditorOpen(false)}
          onSaved={(updated) => {
            setEnriched((prev) => ({ ...prev, ...updated }));
            onPersonUpdated?.(updated);
            setSkillsEditorOpen(false);
          }}
        />
      )}

      {educationEditorOpen && (
        <EducationEditorModal
          person={enriched}
          apiClient={apiClient}
          onClose={() => setEducationEditorOpen(false)}
          onSaved={(updated) => {
            setEnriched((prev) => ({ ...prev, ...updated }));
            onPersonUpdated?.(updated);
            setEducationEditorOpen(false);
          }}
        />
      )}

      {certificationsEditorOpen && (
        <CertificationsEditorModal
          person={enriched}
          apiClient={apiClient}
          onClose={() => setCertificationsEditorOpen(false)}
          onSaved={(updated) => {
            setEnriched((prev) => ({ ...prev, ...updated }));
            onPersonUpdated?.(updated);
            setCertificationsEditorOpen(false);
          }}
        />
      )}

      {languagesEditorOpen && (
        <LanguagesEditorModal
          person={enriched}
          apiClient={apiClient}
          onClose={() => setLanguagesEditorOpen(false)}
          onSaved={(updated) => {
            setEnriched((prev) => ({ ...prev, ...updated }));
            onPersonUpdated?.(updated);
            setLanguagesEditorOpen(false);
          }}
        />
      )}

    </>
  );

  if (isEmbedded) {
    return (
      <div className="w-full" data-testid="candidate-profile-modal">
        {profilePanel}
        {editorLayers}
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="candidate-profile-modal"
    >
      {profilePanel}
      {editorLayers}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
interface HeaderProps {
  person: Person;
  viewerContext: ViewerContext;
  details?: CandidateDetailsResponse;
  onClose: () => void;
  showCloseButton?: boolean;
  reviewQueue?: CandidateReviewQueueContext;
  cvUpload?: ReturnType<typeof usePersonCvUpload>;
  onContactSaved?: (updated: Person) => void;
}

const CandidateModalHeader: React.FC<HeaderProps> = ({
  person,
  viewerContext,
  details,
  onClose,
  showCloseButton = true,
  reviewQueue,
  cvUpload,
  onContactSaved,
}) => {
  const initials = computeInitials(person.Name);
  const chips: Array<{ label: string; variant: 'seniority' | 'management' | 'availability' | 'notice' }> = [];
  if (details?.seniorityLevel) chips.push({ label: details.seniorityLevel, variant: 'seniority' });
  if (person.ManagementLevel) chips.push({ label: person.ManagementLevel, variant: 'management' });
  if (details?.availability.status) chips.push({ label: details.availability.status, variant: 'availability' });
  if (details?.availability.noticePeriodWeeks != null) {
    chips.push({ label: `${details.availability.noticePeriodWeeks} weeks notice`, variant: 'notice' });
  }

  const { user, isAdmin, canEditContacts } = useAuth();
  const [profileUpdateUrl, setProfileUpdateUrl] = useState<string | null>(null);
  const [profileUpdateToken, setProfileUpdateToken] = useState<string | null>(null);
  const [profileUpdateExpires, setProfileUpdateExpires] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [loadingLink, setLoadingLink] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [linkEmailStatus, setLinkEmailStatus] = useState<{ sent: boolean; error?: string } | null>(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const isEmployee = (person.ProfileCategory || '').toLowerCase() === 'employee';
  const hasContactEmailForLink = looksLikeEmail((person.Contact || '').trim());

  const handleGenerateLink = async () => {
    if (person.id == null) return;
    setLoadingLink(true);
    setLinkError(null);
    setLinkEmailStatus(null);
    try {
      const result = await adminApi.createProfileUpdateLink(
        person.id,
        profileUpdateLinkExpiresInDays(person.ProfileCategory)
      );
      setProfileUpdateUrl(result.url);
      setProfileUpdateToken(result.token);
      setProfileUpdateExpires(result.expires_at);
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e && (e as { response?: { data?: { error?: unknown } } }).response?.data?.error;
      setLinkError(typeof msg === 'string' ? msg : 'Failed to create link');
    } finally {
      setLoadingLink(false);
    }
  };

  const handleSendEmail = async () => {
    if (!profileUpdateToken) return;
    setSendingEmail(true);
    setLinkEmailStatus(null);
    try {
      const result = await adminApi.sendProfileUpdateLinkEmail(profileUpdateToken);
      setLinkEmailStatus({
        sent: result.email_sent,
        error: result.email_error,
      });
    } catch (e: unknown) {
      const msg = (e && typeof e === 'object' && 'response' in e ? (e as any).response?.data?.error : undefined) as string | undefined;
      setLinkEmailStatus({ sent: false, error: msg || 'Failed to send email' });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCopyLink = async () => {
    if (!profileUpdateUrl) return;
    try {
      await navigator.clipboard.writeText(profileUpdateUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setLinkError('Copy failed');
    }
  };

  const workspaceDomain =
    user?.email?.includes('@') ? user.email.split('@')[1]!.toLowerCase() : 'creativedock.com';
  const isInternalViewer =
    viewerContext.kind === 'admin' || viewerContext.kind === 'hr';
  const showProfileUpdateSection =
    (isAdmin || canEditContacts) && person.id != null && isInternalViewer;
  return (
    <div className="relative p-6 border-b border-dark-border">
      {reviewQueue && (
        <div className="mb-4 pr-10">
          <ReviewQueueStepper queue={reviewQueue} />
        </div>
      )}
      {showCloseButton && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-dark-text-secondary hover:text-dark-text text-2xl"
          aria-label="Close profile"
          data-testid="candidate-profile-close"
        >
          &times;
        </button>
      )}
      <div className="flex items-start gap-4">
        <div
          className="w-16 h-16 rounded-xl flex-shrink-0 bg-brand-gradient flex items-center justify-center text-white font-bold text-xl"
          aria-hidden="true"
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <h2 className="text-2xl font-bold text-dark-text">{person.Name || 'Unknown'}</h2>
            <ProfileCategoryChip category={person.ProfileCategory} />
          </div>
          <p className="text-base text-dark-text-secondary font-medium mb-2">
            {person.Title || 'Title not specified'}
            {person.PrimaryLocation ? ` · ${person.PrimaryLocation}` : ''}
            {person.Team ? ` · ${person.Team}` : ''}
          </p>
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {chips.map((c, i) => (
                <Chip key={`${c.variant}-${i}`} variant={c.variant}>
                  {c.label}
                </Chip>
              ))}
            </div>
          )}
        </div>

        {((cvUpload && showProfileUpdateActions(viewerContext)) || showProfileUpdateSection) && (
          <div className="flex flex-col items-end gap-2 flex-shrink-0 max-w-md pr-8">
            {cvUpload && showProfileUpdateActions(viewerContext) && (
              <ProfileHeaderUpdateActions viewerContext={viewerContext} cvUpload={cvUpload} />
            )}
            {showProfileUpdateSection && (
              <>
            {!profileUpdateUrl ? (
              <button
                type="button"
                onClick={handleGenerateLink}
                disabled={loadingLink}
                className="px-4 py-2 rounded-lg bg-white text-dark-card border border-dark-border text-sm font-medium hover:bg-gray-100 disabled:opacity-50"
              >
                {loadingLink
                  ? 'Generating…'
                  : isEmployee
                    ? 'Generate profile-update link'
                    : 'Generate link for candidate'}
              </button>
            ) : (
              <div className="flex flex-col items-end gap-2 max-w-md">
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="px-4 py-2 rounded-lg bg-white text-dark-card border border-dark-border text-sm font-medium hover:bg-gray-100"
                  >
                    {copied ? 'Copied!' : 'Copy link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowQrModal(true)}
                    className="px-4 py-2 rounded-lg bg-white text-dark-card border border-dark-border text-sm font-medium hover:bg-gray-100"
                  >
                    Show QR code
                  </button>
                  <button
                    type="button"
                    onClick={handleSendEmail}
                    disabled={sendingEmail || !hasContactEmailForLink}
                    title={
                      hasContactEmailForLink
                        ? undefined
                        : 'Set a valid Contact email below before sending the link'
                    }
                    className="px-4 py-2 rounded-lg bg-white text-dark-card border border-dark-border text-sm font-medium hover:bg-gray-100 disabled:opacity-50"
                  >
                    {sendingEmail
                      ? 'Sending…'
                      : isEmployee
                        ? 'Email link to employee'
                        : 'Send email'}
                  </button>
                  {profileUpdateExpires && (
                    <span className="text-xs text-dark-text-secondary whitespace-nowrap">
                      Expires {new Date(profileUpdateExpires).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <ProfileUpdateLinkHelpText expiresAt={profileUpdateExpires} />
              </div>
            )}
            {linkEmailStatus && (
              <p className={`text-xs ${linkEmailStatus.sent ? 'text-green-400' : 'text-amber-400'}`}>
                {linkEmailStatus.sent ? 'Email sent.' : linkEmailStatus.error ? `Email not sent: ${linkEmailStatus.error}` : ''}
              </p>
            )}
            {linkError && (
              <p className="text-xs text-red-400">{linkError}</p>
            )}

            {(canEditContacts ||
              import.meta.env.VITE_GOOGLE_CLIENT_ID ||
              (person.Contact || '').trim()) && (
              <ProfileHeaderEmailField
                person={person}
                workspaceDomain={workspaceDomain}
                canEdit={canEditContacts && isInternalViewer}
                onContactSaved={onContactSaved}
              />
            )}
              </>
            )}
          </div>
        )}
      </div>

      {showQrModal && profileUpdateUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-80 p-4"
          onClick={() => setShowQrModal(false)}
        >
          <div
            className="bg-dark-card border border-dark-border rounded-2xl p-6 flex flex-col items-center shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-dark-text mb-2">Profile update link – scan to open</h3>
            <p className="text-xs text-dark-text-secondary text-center max-w-sm mb-3 leading-relaxed">
              Scan on the device where they will sign in with the Google account that matches Contact email. After a successful
              profile save, this URL stops working; until then they can reopen it until the expiry date.
            </p>
            <div className="bg-white p-3 rounded-lg mb-4">
              <QRCodeSVG value={profileUpdateUrl} size={200} level="M" />
            </div>
            <button
              type="button"
              onClick={() => setShowQrModal(false)}
              className="px-4 py-2 rounded-lg bg-dark-border text-dark-text text-sm font-medium hover:bg-opacity-80"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Review queue — header stepper
// ---------------------------------------------------------------------------
/**
 * Phase 3F — compact "1 of N · Prev / Next" stepper pinned to the top of the
 * header when the modal is in queue mode. Prev is disabled on the first item;
 * Next is disabled on the last (the footer bar drives completion).
 */
const ReviewQueueStepper: React.FC<{ queue: CandidateReviewQueueContext }> = ({ queue }) => {
  const total = queue.ids.length;
  const atStart = queue.index <= 0;
  const atEnd = queue.index >= total - 1;
  return (
    <div
      className="flex items-center justify-end gap-2"
      data-testid="review-queue-stepper"
    >
      <span className="text-xs text-dark-text-secondary">
        Review queue · <span className="text-dark-text font-semibold">{queue.index + 1}</span> of{' '}
        {total}
      </span>
      <button
        type="button"
        onClick={queue.onPrev}
        disabled={atStart}
        className="text-xs px-2 py-1 rounded-md border border-dark-border text-dark-text-secondary hover:text-dark-text disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="review-queue-prev"
        aria-label="Previous candidate in review queue"
      >
        ← Prev
      </button>
      <button
        type="button"
        onClick={queue.onNext}
        disabled={atEnd}
        className="text-xs px-2 py-1 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="review-queue-next"
        aria-label="Next candidate in review queue"
      >
        Next →
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Review queue — footer action bar
// ---------------------------------------------------------------------------
/**
 * Footer bar shown either when a review queue is driving the modal OR when
 * the candidate currently has status `needs_review`. Inside a queue, the
 * "Needs more work" button advances without patching so reviewers can skip.
 * Outside a queue that skip is meaningless (there's nothing to advance to),
 * so the button is hidden.
 *
 * "Mark reviewed" always patches the server and updates local state; when
 * a queue is present it also advances to the next row on success.
 * "Discard" is a disabled placeholder until delete flows ship in Phase 3E.
 */
interface ReviewActionBarProps {
  reviewStatus: ReviewStatus;
  busy: boolean;
  error: string | null;
  /** Whether the current row is the last in the active queue. Ignored when !inQueue. */
  isLast: boolean;
  /** True when the modal is driven by a review queue. Gates the skip button. */
  inQueue: boolean;
  onMarkReviewed: () => void;
  onNeedsMoreWork: () => void;
  /** Ref forwarded from the parent so ReviewBanner can focus this button. */
  markReviewedBtnRef?: React.RefObject<HTMLButtonElement>;
}

const ReviewActionBar: React.FC<ReviewActionBarProps> = ({
  reviewStatus,
  busy,
  error,
  isLast,
  inQueue,
  onMarkReviewed,
  onNeedsMoreWork,
  markReviewedBtnRef,
}) => (
  <div
    className="px-6 py-4 border-t border-dark-border bg-dark-bg/50"
    data-testid="review-queue-action-bar"
  >
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="text-xs text-dark-text-secondary">Review decision</p>
        <p className="text-sm text-dark-text">
          Confirm parsed values look right, or flag for later.
        </p>
        {error && (
          <p className="text-xs text-red-400 mt-1" role="alert" data-testid="review-queue-error">
            {error}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          ref={markReviewedBtnRef}
          type="button"
          onClick={onMarkReviewed}
          disabled={busy || reviewStatus === 'reviewed'}
          className="px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-emerald-400"
          data-testid="review-queue-mark-reviewed"
        >
          {busy ? 'Saving…' : '✓ Mark reviewed'}
        </button>
        {inQueue && (
          <button
            type="button"
            onClick={onNeedsMoreWork}
            disabled={busy}
            className="px-4 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-dark-text text-sm font-semibold disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-gray-400"
            data-testid="review-queue-needs-work"
          >
            {isLast ? 'Needs more work · Close' : 'Needs more work · Skip →'}
          </button>
        )}
        <button
          type="button"
          disabled
          title="Delete flow ships in Phase 3E"
          className="px-3 py-2 rounded-md border border-dashed border-dark-border text-dark-text-secondary text-sm cursor-not-allowed opacity-60"
          data-testid="review-queue-discard"
        >
          Discard
        </button>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Review banner
// ---------------------------------------------------------------------------
const ReviewBanner: React.FC<{ details: CandidateDetailsResponse; onReviewNow: () => void }> = ({
  details,
  onReviewNow,
}) => {
  const lowConfidenceFields = details.parseConfidence
    ? Object.entries(details.parseConfidence)
        .filter(([, v]) => typeof v === 'number' && v < 0.8)
        .map(([k, v]) => `${k} (${v.toFixed(2)})`)
    : [];
  return (
    <div
      className="mx-6 my-4 rounded-lg bg-amber-950/40 border border-amber-500/50 p-3 flex items-start justify-between gap-3"
      data-testid="candidate-profile-review-banner"
      role="alert"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-amber-200">Needs review</p>
        <p className="text-xs text-dark-text-secondary mt-1">
          {lowConfidenceFields.length > 0
            ? `${lowConfidenceFields.length} field${lowConfidenceFields.length === 1 ? '' : 's'} below threshold after parsing: ${lowConfidenceFields.join(', ')}.`
            : 'Review the fields below before confirming this profile.'}
        </p>
      </div>
      <button
        onClick={onReviewNow}
        className="shrink-0 rounded-md bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold px-3 py-1.5"
        data-testid="candidate-profile-review-now"
      >
        REVIEW NOW
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------
const TabBar: React.FC<{
  activeTab: CandidateProfileTab;
  onSelect: (tab: CandidateProfileTab) => void;
  visibleTabs: CandidateProfileTab[];
}> = ({ activeTab, onSelect, visibleTabs }) => (
  <div
    className="flex gap-1 px-6 border-b border-dark-border"
    role="tablist"
    aria-label="Candidate profile tabs"
  >
    {TABS.filter((t) => visibleTabs.includes(t.id)).map((t) => {
      const active = t.id === activeTab;
      return (
        <button
          key={t.id}
          role="tab"
          aria-selected={active}
          data-testid={`candidate-profile-tab-${t.id}`}
          onClick={() => onSelect(t.id)}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            active
              ? 'border-blue-500 text-dark-text'
              : 'border-transparent text-dark-text-secondary hover:text-dark-text'
          }`}
        >
          {t.label}
        </button>
      );
    })}
  </div>
);

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------
/**
 * Unified profile model: backend always returns a CandidateDetails envelope
 * (with empty arrays + null projection fields for employees that have no
 * structured evidence yet). The Overview tab renders cards only when their
 * backing collection is non-empty. KeyFactsPanel always renders so Email +
 * LinkedIn + directory facts (Title, Location, …) are visible for everyone.
 */
const OverviewTab: React.FC<{
  person: Person;
  details?: CandidateDetailsResponse;
  viewerContext: ViewerContext;
  onEditKeyFacts?: () => void;
  cvUpload: ReturnType<typeof usePersonCvUpload>;
  onEditLanguages?: () => void;
}> = ({ person, details, viewerContext, onEditKeyFacts, cvUpload, onEditLanguages }) => {
  const topSkills = (person.CanonicalSkills ?? []).slice(0, 5);
  const overflowCount = Math.max(0, (person.CanonicalSkills?.length ?? 0) - topSkills.length);

  const hasSummary = !!details?.professionalSummary;
  const hasTargetRoles = (details?.targetRoles?.length ?? 0) > 0;
  const hasDomains = (details?.domains?.length ?? 0) > 0;
  const hasAnyStructuredCard = hasSummary || hasTargetRoles || hasDomains || topSkills.length > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      <div className="space-y-6 min-w-0">
        {showProfileUpdateActions(viewerContext) && (
          <ProfileOverviewUpdateActions viewerContext={viewerContext} cvUpload={cvUpload} />
        )}

        {!hasAnyStructuredCard && (
          <p
            className="text-sm text-dark-text-secondary italic"
            data-testid="candidate-profile-overview-empty"
          >
            No structured profile evidence yet. Upload a CV above or use Edit key facts on the right,
            or ask HR for a profile-update link.
          </p>
        )}

        {hasSummary && (
          <Section title="Professional summary">
            <p className="text-sm text-dark-text whitespace-pre-wrap">{details!.professionalSummary}</p>
          </Section>
        )}

        {topSkills.length > 0 && (
          <Section title="Top canonical skills">
            <div className="flex flex-wrap gap-2" data-testid="candidate-profile-top-skills">
              {topSkills.map((s) => (
                <Chip key={s.id} variant="skill">
                  {s.name}
                </Chip>
              ))}
              {overflowCount > 0 && (
                <Chip variant="skill-overflow">+{overflowCount}</Chip>
              )}
            </div>
          </Section>
        )}

        {hasTargetRoles && (
          <Section title="Target roles">
            <div className="flex flex-wrap gap-2" data-testid="candidate-profile-target-roles">
              {details!.targetRoles.map((r) => (
                <Chip key={r} variant="target-role">
                  {r}
                </Chip>
              ))}
            </div>
          </Section>
        )}

        {hasDomains && (
          <Section title="Domains">
            <div className="flex flex-wrap gap-2" data-testid="candidate-profile-domains">
              {details!.domains.map((d) => (
                <Chip key={d.domain} variant="domain">
                  {d.yearsExperience != null ? `${d.domain} · ${d.yearsExperience}y` : d.domain}
                </Chip>
              ))}
            </div>
          </Section>
        )}
      </div>

      <KeyFactsPanel
        person={person}
        details={details}
        onEditKeyFacts={onEditKeyFacts}
        showEditKeyFacts={showProfileUpdateActions(viewerContext)}
        onEditLanguages={onEditLanguages}
      />
    </div>
  );
};

/**
 * KeyFactsPanel always renders for every person (employee or external_candidate).
 * Email + LinkedIn live at the top and read directly from the people row, so
 * they appear even when no structured CandidateDetails evidence exists.
 */
const KeyFactsPanel: React.FC<{
  person: Person;
  details?: CandidateDetailsResponse;
  onEditKeyFacts?: () => void;
  showEditKeyFacts?: boolean;
  onEditLanguages?: () => void;
}> = ({ person, details, onEditKeyFacts, showEditKeyFacts, onEditLanguages }) => {
  const availabilityLine = details
    ? [
        details.availability.status,
        details.availability.noticePeriodWeeks != null
          ? `${details.availability.noticePeriodWeeks} weeks notice`
          : null,
        details.availability.date,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
  const rateLine = details
    ? details.rate.expectationMin != null || details.rate.expectationMax != null
      ? [details.rate.expectationMin, details.rate.expectationMax]
          .filter((v) => v != null)
          .join('–') + (details.rate.currency ? ` ${details.rate.currency}` : '')
      : null
    : null;
  const managementLine = [person.ManagementLevel, person.AspiringManagementLevel]
    .filter(Boolean)
    .map((v, i) => (i === 1 ? `aspiring: ${v}` : v))
    .join(' · ');

  const isExternal =
    (person.ProfileCategory || '').toLowerCase() === 'external_candidate';

  const contactEmail = (person.Contact || '').trim();
  const linkedInUrl = (person.LinkedInProfileUrl || '').trim();
  const languages = details?.languages ?? [];

  return (
    <aside
      className="bg-dark-bg/60 border border-dark-border rounded-xl p-4 space-y-3 h-fit sticky top-4"
      data-testid="candidate-profile-key-facts"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-bold uppercase tracking-wider text-blue-300">Key facts</h3>
        {showEditKeyFacts && onEditKeyFacts ? (
          <button
            type="button"
            onClick={onEditKeyFacts}
            className="rounded-full bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-500 shrink-0"
            data-testid="candidate-profile-edit-key-facts"
          >
            Edit key facts
          </button>
        ) : null}
      </div>

      <LinkFact
        label="Email"
        value={contactEmail || null}
        href={contactEmail ? `mailto:${contactEmail}` : null}
        testId="candidate-profile-fact-email"
      />
      <LinkFact
        label="LinkedIn"
        value={linkedInUrl ? prettyLinkedIn(linkedInUrl) : null}
        href={linkedInUrl || null}
        external
        testId="candidate-profile-fact-linkedin"
      />

      <Fact label="Primary location" value={person.PrimaryLocation} />
      {details && (
        <>
          <Fact label="Location model prefs" value={details.locationModelPreferences.join(', ')} />
          <Fact label="Employment type prefs" value={details.employmentTypePreferences.join(', ')} />
          <Fact label="Availability" value={availabilityLine || null} />
          <Fact
            label="Experience total"
            value={details.yearsExperienceTotal != null ? `${details.yearsExperienceTotal} years` : null}
          />
          <Fact label="Education level" value={details.educationLevel} />
        </>
      )}
      <Fact label="Management level" value={managementLine || null} />
      {!isExternal && person.ProfessionalInterests && (
        <Fact label="Interests" value={person.ProfessionalInterests} />
      )}
      {!isExternal && person.JobResponsibilities && (
        <Fact label="Current role" value={person.JobResponsibilities} />
      )}
      {details && (
        <>
          <Fact label="Rate country" value={details.rate.country} />
          {rateLine && <Fact label="Rate expectation" value={rateLine} />}
          {details.workAuthorizationRegions.length > 0 && (
            <Fact label="Work authorization" value={details.workAuthorizationRegions.join(', ')} />
          )}
        </>
      )}
      {isExternal && onEditLanguages ? (
        <div data-testid="candidate-profile-languages-section">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="text-xs text-dark-text-secondary">Languages</div>
            <button
              type="button"
              onClick={onEditLanguages}
              className="rounded-full bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-500"
              data-testid="candidate-profile-edit-languages"
            >
              Edit languages
            </button>
          </div>
          {languages.length > 0 ? (
            <div className="flex flex-wrap gap-1.5" data-testid="candidate-profile-languages">
              {languages.map((l) => (
                <Chip key={l.language} variant="language">
                  {l.proficiency ? `${l.language} (${l.proficiency})` : l.language}
                </Chip>
              ))}
            </div>
          ) : (
            <p className="text-xs text-dark-text-secondary" data-testid="candidate-profile-languages-empty">
              No languages recorded.
            </p>
          )}
        </div>
      ) : languages.length > 0 ? (
        <div>
          <div className="text-xs text-dark-text-secondary mb-1">Languages</div>
          <div className="flex flex-wrap gap-1.5" data-testid="candidate-profile-languages">
            {languages.map((l) => (
              <Chip key={l.language} variant="language">
                {l.proficiency ? `${l.language} (${l.proficiency})` : l.language}
              </Chip>
            ))}
          </div>
        </div>
      ) : null}

      {details && isExternal && (
        <div>
          <div className="text-xs text-dark-text-secondary mb-1">Review status</div>
          <ReviewStatusChip status={details.reviewStatus} />
        </div>
      )}
    </aside>
  );
};

// ---------------------------------------------------------------------------
// Experience tab (Commit 3)
// ---------------------------------------------------------------------------
type ExperienceView = 'current' | 'cv_parse' | 'cv_text' | 'candidate_input';

const EXPERIENCE_VIEW_LABELS: Record<ExperienceView, string> = {
  current: 'Current',
  cv_parse: 'CV parse',
  cv_text: 'CV text',
  candidate_input: 'Candidate input',
};

/**
 * Experience tab with shadow-layer view selector (Phase 3E-2).
 *
 * The `Current` layer shows the projected-precedence result that is already
 * on `details.experiences`. The other three layers are fetched lazily the
 * first time the user switches to any non-current view; one request returns
 * all three (cv_parse, cv_text, candidate_input) so subsequent switches are
 * instant. `candidate_input` is hidden when empty per wireframe §E.
 *
 * Only the Current layer exposes the Edit button — edits always land on
 * `admin_edit` rows via PUT, which sit above `cv` in the precedence rules.
 */
const ExperienceTab: React.FC<{
  person: Person;
  details?: CandidateDetailsResponse;
  viewerContext: ViewerContext;
  onEdit: () => void;
}> = ({ person, details, viewerContext, onEdit }) => {
  const [view, setView] = useState<ExperienceView>('current');
  const { layers, loading: layersLoading, error: layersError } =
    useCandidateSourceLayers(person.id, view !== 'current');

  const candidateInputCount = layers?.layers.candidate_input.experiences.length ?? 0;
  const isInternalViewer = viewerContext.kind === 'admin' || viewerContext.kind === 'hr';
  const availableViews: ExperienceView[] = isInternalViewer
    ? [
        'current',
        'cv_parse',
        'cv_text',
        ...(candidateInputCount > 0 ? ['candidate_input' as const] : []),
      ]
    : ['current'];

  return (
    <div data-testid="candidate-profile-experience-tab">
      {/* View selector + Edit button */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div
          role="tablist"
          aria-label="Experience source layer"
          className="inline-flex items-center gap-1 bg-dark-bg border border-dark-border rounded-md p-1"
          data-testid="experience-view-selector"
        >
          {availableViews.map((v) => {
            const active = view === v;
            return (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setView(v)}
                className={`px-3 py-1 text-xs font-medium rounded ${
                  active
                    ? 'bg-indigo-600 text-white'
                    : 'text-dark-text-secondary hover:text-dark-text'
                }`}
                data-testid={`experience-view-${v}`}
              >
                {EXPERIENCE_VIEW_LABELS[v]}
              </button>
            );
          })}
        </div>
        {view === 'current' && (
          <button
            type="button"
            onClick={onEdit}
            className="px-3 py-1 rounded-md text-xs font-bold bg-indigo-600 hover:bg-indigo-500 text-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
            data-testid="candidate-profile-edit-experience"
          >
            Edit experience
          </button>
        )}
      </div>

      {view === 'current' && !details && (
        <p
          className="text-dark-text-secondary text-sm mb-4"
          data-testid="candidate-profile-experience-loading"
        >
          Candidate details are still loading or unavailable. You can still open{' '}
          <strong className="text-dark-text">Edit experience</strong> — the editor uses the latest
          loaded profile data (may start empty until this finishes loading).
        </p>
      )}

      {view === 'current' && details && (
        <ExperienceListView experiences={details.experiences} />
      )}

      {view !== 'current' && layersLoading && (
        <p
          className="text-sm text-dark-text-secondary"
          data-testid="experience-view-loading"
        >
          Loading source layers…
        </p>
      )}
      {view !== 'current' && layersError && (
        <p
          className="text-sm text-red-400"
          role="alert"
          data-testid="experience-view-error"
        >
          {layersError}
        </p>
      )}
      {view === 'cv_parse' && layers && (
        <ExperienceListView experiences={layers.layers.cv_parse.experiences} />
      )}
      {view === 'candidate_input' && layers && (
        <ExperienceListView experiences={layers.layers.candidate_input.experiences} />
      )}
      {view === 'cv_text' && layers && (
        <CvTextView cvText={layers.layers.cv_text} />
      )}
    </div>
  );
};

const ExperienceListView: React.FC<{ experiences: CandidateExperienceEntry[] }> = ({
  experiences,
}) => {
  if (experiences.length === 0) {
    return (
      <div
        className="text-center py-12 text-dark-text-secondary text-sm"
        data-testid="candidate-profile-experience-empty"
      >
        No experience entries in this layer.
      </div>
    );
  }
  const today = new Date();
  const tenYearsAgo = new Date(today.getFullYear() - 10, today.getMonth(), today.getDate());
  return (
    <ol
      className="relative border-l-2 border-dashed border-dark-border ml-4 space-y-6"
      data-testid="candidate-profile-experience-list"
    >
      {experiences.map((exp) => (
        <ExperienceRow key={exp.id} exp={exp} tenYearsAgo={tenYearsAgo} />
      ))}
    </ol>
  );
};

const CvTextView: React.FC<{ cvText: CvTextLayer | null }> = ({ cvText }) => {
  if (!cvText || !cvText.rawText) {
    return (
      <div
        className="text-center py-12 text-dark-text-secondary text-sm"
        data-testid="candidate-profile-cv-text-empty"
      >
        No raw CV text captured for this candidate.
      </div>
    );
  }
  const header = [
    cvText.fileName,
    cvText.sourceKind,
    cvText.parserVersion && `parser ${cvText.parserVersion}`,
    cvText.languageDetected && `lang ${cvText.languageDetected}`,
    cvText.textLength != null && `${cvText.textLength} chars`,
    cvText.importedAt && `imported ${cvText.importedAt.substring(0, 10)}`,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div data-testid="candidate-profile-cv-text">
      {header && (
        <div className="mb-3 text-xs text-dark-text-secondary">{header}</div>
      )}
      <pre
        className="whitespace-pre-wrap break-words text-xs text-dark-text font-mono bg-dark-bg border border-dark-border rounded-md p-4 max-h-[60vh] overflow-y-auto"
        data-testid="candidate-profile-cv-text-body"
      >
        {cvText.rawText}
      </pre>
    </div>
  );
};

const ExperienceRow: React.FC<{ exp: CandidateExperienceEntry; tenYearsAgo: Date }> = ({
  exp,
  tenYearsAgo,
}) => {
  const dotColor = dotColorFor(exp, tenYearsAgo);
  const headline = [exp.roleTitle, exp.companyName].filter(Boolean).join(' · ');
  const metaLine = [
    formatDateRange(exp.startDate, exp.endDate, exp.isCurrent),
    exp.location,
    exp.locationModel,
    exp.senioritySignal,
    exp.managementSignal,
  ]
    .filter(Boolean)
    .join(' · ');
  const provenance = [
    exp.source,
    typeof exp.confidence === 'number' ? exp.confidence.toFixed(2) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <li className="relative pl-6" data-testid="candidate-profile-experience-row">
      <span
        className={`absolute -left-[7px] top-2 block w-3 h-3 rounded-full ${dotColor}`}
        aria-hidden="true"
      />
      <div className="bg-dark-bg/60 border border-dark-border rounded-lg p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h4 className="text-sm font-bold text-blue-300">{headline || 'Unknown role'}</h4>
            {metaLine && <p className="text-xs text-dark-text-secondary mt-0.5">{metaLine}</p>}
          </div>
          {provenance && (
            <span
              className="shrink-0 text-[11px] text-dark-text-secondary whitespace-nowrap"
              data-testid="candidate-profile-experience-provenance"
            >
              {provenance}
            </span>
          )}
        </div>
        {exp.summary && (
          <p className="text-sm text-dark-text mt-2 whitespace-pre-wrap">{exp.summary}</p>
        )}
        {exp.responsibilities.length > 0 && (
          <ul className="list-disc list-inside text-xs text-dark-text-secondary mt-2 space-y-0.5">
            {exp.responsibilities.map((r, i) => (
              <li key={`resp-${i}`}>{r}</li>
            ))}
          </ul>
        )}
        {exp.achievements.length > 0 && (
          <div className="mt-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300 mb-1">
              Achievements
            </div>
            <ul className="list-disc list-inside text-xs text-dark-text space-y-0.5">
              {exp.achievements.map((a, i) => (
                <li key={`ach-${i}`}>{a}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </li>
  );
};

function dotColorFor(exp: CandidateExperienceEntry, tenYearsAgo: Date): string {
  if (exp.isCurrent || !exp.endDate) return 'bg-emerald-500';
  const end = new Date(exp.endDate);
  if (Number.isNaN(end.getTime())) return 'bg-slate-500';
  return end >= tenYearsAgo ? 'bg-blue-500' : 'bg-slate-500';
}

function formatDateRange(
  startIso: string | null,
  endIso: string | null,
  isCurrent: boolean
): string | null {
  const start = formatMonthYear(startIso);
  const end = isCurrent || !endIso ? 'present' : formatMonthYear(endIso);
  if (!start && (!end || end === 'present')) return null;
  return `${start ?? '—'} → ${end ?? '—'}`;
}

function formatMonthYear(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

// ---------------------------------------------------------------------------
// Skills tab (Commit 4) — now with shadow-layer view selector (3E-3 C4)
// ---------------------------------------------------------------------------
type SkillsView = 'current' | 'cv_parse' | 'cv_text' | 'candidate_input';

const SKILLS_VIEW_LABELS: Record<SkillsView, string> = {
  current: 'Current',
  cv_parse: 'CV parse',
  cv_text: 'CV text',
  candidate_input: 'Candidate input',
};

const SkillsListView: React.FC<{
  skills: CandidateSkillEntry[];
  domains: CandidateDomainEntry[];
  emptyLabel: string;
}> = ({ skills, domains, emptyLabel }) => {
  if (skills.length === 0 && domains.length === 0) {
    return (
      <div
        className="text-center py-12 text-dark-text-secondary text-sm"
        data-testid="candidate-profile-skills-layer-empty"
      >
        {emptyLabel}
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {skills.length > 0 && (
        <Section title={`Skills (${skills.length})`}>
          <ul className="space-y-1.5" data-testid="candidate-profile-skills-rich">
            {skills.map((s) => {
              const meta = [
                s.role,
                s.proficiency,
                s.yearsExperience != null ? `${s.yearsExperience} yrs` : null,
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <li
                  key={`${s.skillId}-${s.role ?? ''}`}
                  className="flex items-center justify-between gap-3 px-2 py-1 rounded bg-dark-bg/40 border border-dark-border"
                  data-testid="candidate-profile-skills-rich-row"
                >
                  <span className="text-sm text-dark-text font-medium">
                    {s.skillName ?? s.skillId}
                  </span>
                  {meta && <span className="text-xs text-dark-text-secondary">{meta}</span>}
                </li>
              );
            })}
          </ul>
        </Section>
      )}
      {domains.length > 0 && (
        <Section title={`Domains (${domains.length})`}>
          <div className="flex flex-wrap gap-2" data-testid="candidate-profile-domains-rich">
            {domains.map((d, i) => {
              const label =
                d.yearsExperience != null
                  ? `${d.domain} · ${d.yearsExperience} yrs`
                  : d.domain;
              return (
                <Chip key={`${d.domain}-${i}`} variant="skill">
                  {label}
                </Chip>
              );
            })}
          </div>
        </Section>
      )}
    </div>
  );
};

const SkillsTab: React.FC<{
  person: Person;
  details?: CandidateDetailsResponse;
  viewerContext: ViewerContext;
  onEdit: () => void;
}> = ({ person, details, viewerContext, onEdit }) => {
  const [view, setView] = useState<SkillsView>('current');
  const { layers, loading: layersLoading, error: layersError } =
    useCandidateSourceLayers(person.id, view !== 'current');

  const canonical = person.CanonicalSkills ?? [];
  // SkillExtractions rows without canonical_skill_id represent phrase-only
  // evidence from legacy imports — wireframe variant D calls these out with a
  // dashed border to signal lower confidence.
  const phraseOnly = (person.SkillExtractions ?? []).filter((e) => !e.canonicalSkillId);
  const richSkills = details?.skills ?? [];
  const richDomains = details?.domains ?? [];

  // Fallback for employees with no structured data yet
  const isEmployee = (person.ProfileCategory || '').toLowerCase() === 'employee';
  const hasStructuredSkills =
    richSkills.length > 0 ||
    richDomains.length > 0 ||
    canonical.length > 0 ||
    phraseOnly.length > 0;

  const legacyExpertise = segmentLegacySkills(person.Expertise);
  const legacyAdditional = segmentLegacySkills(person.AdditionalSkills);
  const legacyTools = segmentLegacySkills(person.AIToolExperience);
  const legacyInterests = segmentLegacySkills(person.ProfessionalInterests);

  const hasLegacySkills =
    legacyExpertise.length > 0 ||
    legacyAdditional.length > 0 ||
    legacyTools.length > 0 ||
    legacyInterests.length > 0;

  const candidateInputHasSkills =
    (layers?.layers.candidate_input.skills.length ?? 0) > 0 ||
    (layers?.layers.candidate_input.domains.length ?? 0) > 0;
  const isInternalViewer = viewerContext.kind === 'admin' || viewerContext.kind === 'hr';
  const availableViews: SkillsView[] = isInternalViewer
    ? [
        'current',
        'cv_parse',
        'cv_text',
        ...(candidateInputHasSkills ? ['candidate_input' as const] : []),
      ]
    : ['current'];

  const editButton = (
    <button
      type="button"
      onClick={onEdit}
      className="px-3 py-1 rounded-md text-xs font-bold bg-indigo-600 hover:bg-indigo-500 text-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
      data-testid="candidate-profile-skills-edit"
    >
      Edit skills &amp; domains
    </button>
  );

  const viewSelector = (
    <div
      role="tablist"
      aria-label="Skills source layer"
      className="inline-flex items-center gap-1 bg-dark-bg border border-dark-border rounded-md p-1"
      data-testid="skills-view-selector"
    >
      {availableViews.map((v) => {
        const active = view === v;
        return (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => setView(v)}
            className={`px-3 py-1 text-xs font-medium rounded ${
              active
                ? 'bg-indigo-600 text-white'
                : 'text-dark-text-secondary hover:text-dark-text'
            }`}
            data-testid={`skills-view-${v}`}
          >
            {SKILLS_VIEW_LABELS[v]}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="candidate-profile-skills-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {viewSelector}
        {view === 'current' && editButton}
      </div>

      {view !== 'current' && layersLoading && (
        <p
          className="text-sm text-dark-text-secondary"
          data-testid="skills-view-loading"
        >
          Loading source layers…
        </p>
      )}
      {view !== 'current' && layersError && (
        <p
          className="text-sm text-red-400"
          role="alert"
          data-testid="skills-view-error"
        >
          {layersError}
        </p>
      )}

      {view === 'current' &&
        (!hasStructuredSkills && !hasLegacySkills ? (
          <div
            className="text-center py-12 text-dark-text-secondary text-sm"
            data-testid="candidate-profile-skills-empty"
          >
            No skills captured yet.
          </div>
        ) : (
          <div className="space-y-6">
            {richSkills.length > 0 && (
              <Section title={`Skills (${richSkills.length})`}>
                <ul className="space-y-1.5" data-testid="candidate-profile-skills-rich">
                  {richSkills.map((s) => {
                    const meta = [
                      s.role,
                      s.proficiency,
                      s.yearsExperience != null ? `${s.yearsExperience} yrs` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ');
                    return (
                      <li
                        key={s.skillId}
                        className="flex items-center justify-between gap-3 px-2 py-1 rounded bg-dark-bg/40 border border-dark-border"
                        data-testid="candidate-profile-skills-rich-row"
                      >
                        <span className="text-sm text-dark-text font-medium">
                          {s.skillName ?? s.skillId}
                        </span>
                        {meta && (
                          <span className="text-xs text-dark-text-secondary">{meta}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </Section>
            )}
            {canonical.length > 0 && richSkills.length === 0 && (
              <Section title={`Canonical skills (${canonical.length})`}>
                <div
                  className="flex flex-wrap gap-2"
                  data-testid="candidate-profile-skills-canonical"
                >
                  {canonical.map((s) => (
                    <Chip key={s.id} variant="skill">
                      {s.name}
                    </Chip>
                  ))}
                </div>
              </Section>
            )}
            {richDomains.length > 0 && (
              <Section title={`Domains (${richDomains.length})`}>
                <div
                  className="flex flex-wrap gap-2"
                  data-testid="candidate-profile-domains-rich"
                >
                  {richDomains.map((d, i) => {
                    const label =
                      d.yearsExperience != null
                        ? `${d.domain} · ${d.yearsExperience} yrs`
                        : d.domain;
                    return (
                      <Chip key={`${d.domain}-${i}`} variant="skill">
                        {label}
                      </Chip>
                    );
                  })}
                </div>
              </Section>
            )}
            {phraseOnly.length > 0 && (
              <Section title={`Parsed expertise — unmapped (${phraseOnly.length})`}>
                <p className="text-xs text-dark-text-secondary mb-2">
                  Phrase-only evidence: not linked to the canonical skill graph yet.
                </p>
                <div
                  className="flex flex-wrap gap-2"
                  data-testid="candidate-profile-skills-phrase-only"
                >
                  {phraseOnly.map((e) => (
                    <span
                      key={e.id}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-slate-800 border border-dashed border-dark-border text-dark-text-secondary"
                    >
                      {e.rawPhrase}
                    </span>
                  ))}
                </div>
              </Section>
            )}

            {/* Defensive fallback for employees with no structured data */}
            {isEmployee && !hasStructuredSkills && hasLegacySkills && (
              <div className="space-y-6">
                <div className="px-3 py-2 rounded bg-amber-900/20 border border-amber-500/20 text-xs text-amber-200/80">
                  Legacy directory data — pending migration to structured skills.
                </div>
                {legacyExpertise.length > 0 && (
                  <Section title="Expertise">
                    <div className="flex flex-wrap gap-2">
                      {legacyExpertise.map((s, i) => (
                        <Chip key={`exp-${i}`} variant="skill">
                          {s}
                        </Chip>
                      ))}
                    </div>
                  </Section>
                )}
                {legacyAdditional.length > 0 && (
                  <Section title="Additional skills">
                    <div className="flex flex-wrap gap-2">
                      {legacyAdditional.map((s, i) => (
                        <Chip key={`add-${i}`} variant="skill">
                          {s}
                        </Chip>
                      ))}
                    </div>
                  </Section>
                )}
                {legacyTools.length > 0 && (
                  <Section title="AI tools">
                    <div className="flex flex-wrap gap-2">
                      {legacyTools.map((s, i) => (
                        <Chip key={`tool-${i}`} variant="skill">
                          {s}
                        </Chip>
                      ))}
                    </div>
                  </Section>
                )}
                {legacyInterests.length > 0 && (
                  <Section title="Interests">
                    <div className="flex flex-wrap gap-2">
                      {legacyInterests.map((s, i) => (
                        <Chip key={`int-${i}`} variant="skill">
                          {s}
                        </Chip>
                      ))}
                    </div>
                  </Section>
                )}
              </div>
            )}
          </div>
        ))}

      {view === 'cv_parse' && layers && (
        <SkillsListView
          skills={layers.layers.cv_parse.skills}
          domains={layers.layers.cv_parse.domains}
          emptyLabel="No skills or domains parsed from the CV."
        />
      )}
      {view === 'candidate_input' && layers && (
        <SkillsListView
          skills={layers.layers.candidate_input.skills}
          domains={layers.layers.candidate_input.domains}
          emptyLabel="The candidate hasn't submitted any skills yet."
        />
      )}
      {view === 'cv_text' && layers && <CvTextView cvText={layers.layers.cv_text} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Education tab (Commit 4) — now with shadow-layer view selector (3E-3 C4)
// ---------------------------------------------------------------------------
type EducationView = 'current' | 'cv_parse' | 'cv_text' | 'candidate_input';

const EDUCATION_VIEW_LABELS: Record<EducationView, string> = {
  current: 'Current',
  cv_parse: 'CV parse',
  cv_text: 'CV text',
  candidate_input: 'Candidate input',
};

const EducationCertsListView: React.FC<{
  education: CandidateEducationEntry[];
  certifications: CandidateCertificationEntry[];
  emptyLabel: string;
}> = ({ education, certifications, emptyLabel }) => {
  if (education.length === 0 && certifications.length === 0) {
    return (
      <div
        className="text-center py-12 text-dark-text-secondary text-sm"
        data-testid="candidate-profile-education-layer-empty"
      >
        {emptyLabel}
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {education.length > 0 && (
        <Section title={`Education (${education.length})`}>
          <ul className="space-y-3" data-testid="candidate-profile-education-list">
            {education.map((ed) => {
              const headline = [ed.degree, ed.fieldOfStudy].filter(Boolean).join(' — ');
              const metaLine = [
                formatDateRange(ed.startDate, ed.endDate, false),
                ed.institution,
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <li
                  key={ed.id}
                  className="bg-dark-bg/60 border border-dark-border rounded-lg p-4"
                  data-testid="candidate-profile-education-row"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h4 className="text-sm font-bold text-blue-300">
                        {headline || ed.institution || 'Education'}
                      </h4>
                      {metaLine && (
                        <p className="text-xs text-dark-text-secondary mt-0.5">{metaLine}</p>
                      )}
                    </div>
                    <span className="shrink-0 text-[11px] text-dark-text-secondary whitespace-nowrap">
                      {ed.source}
                      {typeof ed.confidence === 'number'
                        ? ` · ${ed.confidence.toFixed(2)}`
                        : ''}
                    </span>
                  </div>
                  {ed.summary && (
                    <p className="text-sm text-dark-text mt-2 whitespace-pre-wrap">
                      {ed.summary}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {certifications.length > 0 && (
        <Section title={`Certifications (${certifications.length})`}>
          <ul className="space-y-3" data-testid="candidate-profile-certifications-list">
            {certifications.map((c) => {
              const metaLine = [
                c.issuer,
                formatDateRange(c.issuedDate, c.expiresDate, false),
                c.credentialId ? `id: ${c.credentialId}` : null,
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <li
                  key={c.id}
                  className="bg-dark-bg/60 border border-dark-border rounded-lg p-4"
                  data-testid="candidate-profile-certification-row"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h4 className="text-sm font-bold text-blue-300">{c.name}</h4>
                      {metaLine && (
                        <p className="text-xs text-dark-text-secondary mt-0.5">
                          {metaLine}
                        </p>
                      )}
                    </div>
                    {c.credentialUrl && (
                      <a
                        href={c.credentialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 text-xs font-medium text-blue-400 hover:text-blue-300"
                      >
                        View credential →
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Section>
      )}
    </div>
  );
};

const EducationTab: React.FC<{
  person: Person;
  details?: CandidateDetailsResponse;
  viewerContext: ViewerContext;
  onEditEducation: () => void;
  onEditCertifications: () => void;
}> = ({ person, details, viewerContext, onEditEducation, onEditCertifications }) => {
  const [view, setView] = useState<EducationView>('current');
  const { layers, loading: layersLoading, error: layersError } =
    useCandidateSourceLayers(person.id, view !== 'current');

  const candidateInputHasEduCerts =
    (layers?.layers.candidate_input.education.length ?? 0) > 0 ||
    (layers?.layers.candidate_input.certifications.length ?? 0) > 0;
  const isInternalViewer = viewerContext.kind === 'admin' || viewerContext.kind === 'hr';
  const availableViews: EducationView[] = isInternalViewer
    ? [
        'current',
        'cv_parse',
        'cv_text',
        ...(candidateInputHasEduCerts ? ['candidate_input' as const] : []),
      ]
    : ['current'];

  const editEduButton = (
    <button
      type="button"
      onClick={onEditEducation}
      className="px-3 py-1 rounded-md text-xs font-bold bg-indigo-600 hover:bg-indigo-500 text-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
      data-testid="candidate-profile-edit-education"
    >
      Edit education
    </button>
  );
  const editCertsButton = (
    <button
      type="button"
      onClick={onEditCertifications}
      className="px-3 py-1 rounded-md text-xs font-bold bg-indigo-600 hover:bg-indigo-500 text-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
      data-testid="candidate-profile-edit-certifications"
    >
      Edit certifications
    </button>
  );

  const viewSelector = (
    <div
      role="tablist"
      aria-label="Education source layer"
      className="inline-flex items-center gap-1 bg-dark-bg border border-dark-border rounded-md p-1"
      data-testid="education-view-selector"
    >
      {availableViews.map((v) => {
        const active = view === v;
        return (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => setView(v)}
            className={`px-3 py-1 text-xs font-medium rounded ${
              active
                ? 'bg-indigo-600 text-white'
                : 'text-dark-text-secondary hover:text-dark-text'
            }`}
            data-testid={`education-view-${v}`}
          >
            {EDUCATION_VIEW_LABELS[v]}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="candidate-profile-education-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {viewSelector}
        {view === 'current' && (
          <div className="flex items-center gap-2">
            {editEduButton}
            {editCertsButton}
          </div>
        )}
      </div>

      {view !== 'current' && layersLoading && (
        <p
          className="text-sm text-dark-text-secondary"
          data-testid="education-view-loading"
        >
          Loading source layers…
        </p>
      )}
      {view !== 'current' && layersError && (
        <p
          className="text-sm text-red-400"
          role="alert"
          data-testid="education-view-error"
        >
          {layersError}
        </p>
      )}

      {view === 'current' && !details && (
        <p
          className="text-dark-text-secondary text-sm mb-4"
          data-testid="candidate-profile-education-loading"
        >
          Candidate details are still loading or unavailable. You can still use{' '}
          <strong className="text-dark-text">Edit education</strong> /{' '}
          <strong className="text-dark-text">Edit certifications</strong>.
        </p>
      )}

      {view === 'current' &&
        details &&
        (details.education.length === 0 && details.certifications.length === 0 ? (
          <div
            className="text-center py-12 text-dark-text-secondary text-sm"
            data-testid="candidate-profile-education-empty"
          >
            No education or certifications captured for this candidate.
          </div>
        ) : (
          <EducationCertsListView
            education={details.education}
            certifications={details.certifications}
            emptyLabel=""
          />
        ))}

      {view === 'cv_parse' && layers && (
        <EducationCertsListView
          education={layers.layers.cv_parse.education}
          certifications={layers.layers.cv_parse.certifications}
          emptyLabel="No education or certifications parsed from the CV."
        />
      )}
      {view === 'candidate_input' && layers && (
        <EducationCertsListView
          education={layers.layers.candidate_input.education}
          certifications={layers.layers.candidate_input.certifications}
          emptyLabel="The candidate hasn't submitted education or certifications yet."
        />
      )}
      {view === 'cv_text' && layers && <CvTextView cvText={layers.layers.cv_text} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Pools & Source tab (Commit 5)
// ---------------------------------------------------------------------------
const PoolsSourceTab: React.FC<{
  person: Person;
  details?: CandidateDetailsResponse;
  onNavigateToCandidatePool?: (poolId: number) => void;
}> = ({ person, details, onNavigateToCandidatePool }) => {
  const [memberships, setMemberships] = useState<PersonPoolMembership[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (person.id == null) return;
    let cancelled = false;
    setMemberships(null);
    setError(null);
    listPoolMembershipsForPerson(person.id)
      .then((rows) => {
        if (!cancelled) setMemberships(rows);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load memberships');
      });
    return () => {
      cancelled = true;
    };
  }, [person.id]);

  const sourcePoolId = details?.sourceMetadata.poolId ?? null;

  return (
    <div className="space-y-6" data-testid="candidate-profile-pools-source">
      <Section title="Pool memberships">
        {memberships == null && !error && (
          <p className="text-xs text-dark-text-secondary" data-testid="candidate-profile-pools-loading">
            Loading memberships…
          </p>
        )}
        {error && (
          <p className="text-xs text-red-400" data-testid="candidate-profile-pools-error">
            {error}
          </p>
        )}
        {memberships != null && memberships.length === 0 && (
          <p
            className="text-xs text-dark-text-secondary"
            data-testid="candidate-profile-pools-empty"
          >
            {person.ProfileCategory === 'employee'
              ? 'Not yet linked to the Internal directory pool. Contact an admin if this persists.'
              : 'This candidate is not a member of any pool.'}
          </p>
        )}
        {memberships != null && memberships.length > 0 && (
          <ul className="space-y-2" data-testid="candidate-profile-pools-list">
            {memberships.map((m) => {
              const isSource = sourcePoolId === m.pool_id;
              return (
                <li
                  key={m.pool_id}
                  className="flex items-center justify-between gap-3 bg-dark-bg/60 border border-dark-border rounded-lg px-3 py-2"
                  data-testid="candidate-profile-pools-row"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {onNavigateToCandidatePool ? (
                        <button
                          className="text-sm font-medium text-blue-400 hover:text-blue-300 truncate"
                          onClick={() => onNavigateToCandidatePool(m.pool_id)}
                        >
                          {m.pool_name}
                        </button>
                      ) : (
                        <span className="text-sm font-medium text-dark-text truncate">{m.pool_name}</span>
                      )}
                      {isSource && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-900/40 border border-emerald-500/50 text-emerald-200">
                          source
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-dark-text-secondary mt-0.5">
                      added {formatDateIso(m.added_at) ?? m.added_at} · by {m.added_by} · {m.source_kind}
                    </div>
                  </div>
                  <span className="text-[11px] uppercase tracking-wider text-dark-text-secondary shrink-0">
                    {m.my_role}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Section title="Import source">
        {details?.sourceMetadata &&
        (details.sourceMetadata.originalFilename ||
          details.sourceMetadata.parsedAt ||
          details.parserVersion) ? (
          <dl
            className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm"
            data-testid="candidate-profile-source-metadata"
          >
            <dt className="text-xs text-dark-text-secondary">Parser</dt>
            <dd className="text-dark-text">{details.parserVersion ?? '—'}</dd>

            <dt className="text-xs text-dark-text-secondary">Original filename</dt>
            <dd className="text-dark-text">{details.sourceMetadata.originalFilename ?? '—'}</dd>

            <dt className="text-xs text-dark-text-secondary">Parsed at</dt>
            <dd className="text-dark-text">
              {details.sourceMetadata.parsedAt
                ? formatDateIso(details.sourceMetadata.parsedAt) ?? details.sourceMetadata.parsedAt
                : '—'}
            </dd>

            {sourcePoolId != null && (
              <>
                <dt className="text-xs text-dark-text-secondary">Source pool</dt>
                <dd className="text-dark-text">
                  {onNavigateToCandidatePool ? (
                    <button
                      className="text-blue-400 hover:text-blue-300"
                      onClick={() => onNavigateToCandidatePool(sourcePoolId)}
                    >
                      #{sourcePoolId} — open →
                    </button>
                  ) : (
                    `#${sourcePoolId}`
                  )}
                </dd>
              </>
            )}
          </dl>
        ) : (
          <p
            className="text-xs text-dark-text-secondary"
            data-testid="candidate-profile-source-empty"
          >
            No import source metadata recorded.
          </p>
        )}
      </Section>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Provenance footer
// ---------------------------------------------------------------------------
const ProvenanceFooter: React.FC<{ details?: CandidateDetailsResponse }> = ({ details }) => {
  if (!details) return null;
  const parts = [
    details.parserVersion ? `parsed by ${details.parserVersion}` : null,
    details.sourceMetadata.parsedAt ? formatDateIso(details.sourceMetadata.parsedAt) : null,
    details.sourceMetadata.originalFilename ? `source: ${details.sourceMetadata.originalFilename}` : null,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <footer
      className="px-6 py-3 border-t border-dark-border text-xs text-dark-text-secondary"
      data-testid="candidate-profile-provenance"
    >
      {parts.join(' · ')}
    </footer>
  );
};

// ---------------------------------------------------------------------------
// Small leaf components
// ---------------------------------------------------------------------------
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section>
    <h3 className="text-xs font-bold uppercase tracking-wider text-blue-300 mb-2">{title}</h3>
    {children}
  </section>
);

const Fact: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs text-dark-text-secondary">{label}</div>
      <div className="text-sm text-dark-text">{value}</div>
    </div>
  );
};

/**
 * Like Fact, but renders the value as an anchor when href is provided. Used
 * for Email (mailto:) and LinkedIn (external) on the unified KeyFactsPanel —
 * these always render so an employee with no structured CV still has a
 * direct contact link visible on the Overview tab.
 */
const LinkFact: React.FC<{
  label: string;
  value: string | null | undefined;
  href: string | null | undefined;
  external?: boolean;
  testId?: string;
}> = ({ label, value, href, external, testId }) => {
  if (!value) return null;
  return (
    <div data-testid={testId}>
      <div className="text-xs text-dark-text-secondary">{label}</div>
      {href ? (
        <a
          href={href}
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          className="text-sm text-blue-400 hover:text-blue-300 underline-offset-2 hover:underline break-all"
        >
          {value}
        </a>
      ) : (
        <div className="text-sm text-dark-text break-all">{value}</div>
      )}
    </div>
  );
};

/** Strip protocol + trailing slash for compact display of LinkedIn URLs. */
function prettyLinkedIn(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

/**
 * Header chip showing the profile category. Unified profile model: every
 * person row (employee or external_candidate) renders the same modal — this
 * chip just labels which kind it is so admins can tell at a glance. Hidden
 * when the category is unknown to avoid showing a misleading default.
 */
const ProfileCategoryChip: React.FC<{ category?: string | null }> = ({ category }) => {
  const normalized = (category || '').toLowerCase();
  if (normalized === 'external_candidate') {
    return (
      <span
        className="px-2 py-0.5 rounded-md text-xs font-medium bg-amber-900/40 text-amber-100 border border-amber-500/30"
        data-testid="candidate-profile-category-chip"
        data-category="external_candidate"
      >
        External candidate
      </span>
    );
  }
  if (normalized === 'employee') {
    return (
      <span
        className="px-2 py-0.5 rounded-md text-xs font-medium bg-blue-900/40 text-blue-100 border border-blue-500/30"
        data-testid="candidate-profile-category-chip"
        data-category="employee"
      >
        Employee
      </span>
    );
  }
  return null;
};

type ChipVariant =
  | 'seniority'
  | 'management'
  | 'availability'
  | 'notice'
  | 'skill'
  | 'skill-overflow'
  | 'target-role'
  | 'domain'
  | 'language';

const CHIP_STYLES: Record<ChipVariant, string> = {
  seniority: 'bg-emerald-900/40 border border-emerald-500/60 text-dark-text',
  management: 'bg-slate-800 border border-indigo-500/60 text-dark-text',
  availability: 'bg-green-900/50 border border-green-500/60 text-dark-text',
  notice: 'bg-slate-800 border border-dark-border text-dark-text-secondary',
  skill: 'bg-blue-900/50 border border-blue-500/40 text-dark-text',
  'skill-overflow': 'bg-dark-bg border border-dark-border text-dark-text-secondary',
  'target-role': 'bg-emerald-900/40 border border-emerald-500/40 text-dark-text',
  domain: 'bg-indigo-900/40 border border-indigo-500/40 text-dark-text',
  language: 'bg-indigo-900/40 border border-indigo-500/40 text-dark-text',
};

const Chip: React.FC<{ variant: ChipVariant; children: React.ReactNode }> = ({ variant, children }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs ${CHIP_STYLES[variant]}`}>
    {children}
  </span>
);

const ReviewStatusChip: React.FC<{ status: ReviewStatus }> = ({ status }) => {
  const map: Record<ReviewStatus, { label: string; cls: string }> = {
    unreviewed: { label: 'unreviewed', cls: 'bg-slate-800 border border-dark-border text-dark-text-secondary' },
    needs_review: { label: 'needs_review', cls: 'bg-amber-700 border border-amber-500/70 text-white' },
    reviewed: { label: 'reviewed', cls: 'bg-emerald-900/50 border border-emerald-500/60 text-dark-text' },
  };
  const style = map[status];
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.cls}`}
      data-testid="candidate-profile-review-status"
    >
      {style.label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function computeInitials(name: string | null | undefined): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDateIso(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function segmentLegacySkills(text: string | null | undefined): string[] {
  if (!text) return [];
  return text
    .split(/[,;\n\r]/)
    .map((s) => s.trim())
    .filter(
      (s) =>
        s.length > 0 &&
        !/^(n\/a|n\/a\.|none|none listed|none specified|none provided|not specified|not provided|details missing|not available|unknown|—|-)$/i.test(
          s,
        ),
    );
}

export { computeInitials };
